
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RiskQuestion } from './RiskQuestion';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Application } from '@/types/database.types';

interface EmploymentPracticesProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

export const EmploymentPractices = ({ register, setValue, watch }: EmploymentPracticesProps) => {
  const hasLayoffs = watch('has_recent_layoffs') as boolean | undefined;
  const hasPlannedLayoffs = watch('has_planned_layoffs') as boolean | undefined;
  const hasForeignTravel = watch('has_foreign_travel') as boolean | undefined;
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Employment Practices</h3>
      
      <RiskQuestion
        id="has_recent_layoffs"
        label="Has your company had any layoffs in the past 12 months?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="recent_layoffs_details"
        detailsPlaceholder="Please provide details about recent layoffs including number of employees and dates..."
      />
      
      {hasLayoffs && (
        <div className="pl-5 border-l-2 border-primary/20 space-y-3 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="layoff_count">How many employees were laid off?</Label>
              <Input
                id="layoff_count"
                type="number"
                {...register('layoff_count')}
                placeholder="Number of employees"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="layoff_date">When did the layoffs occur?</Label>
              <Input
                id="layoff_date"
                type="date"
                {...register('layoff_date')}
              />
            </div>
          </div>
        </div>
      )}
      
      <RiskQuestion
        id="has_planned_layoffs"
        label="Does your company anticipate any layoffs in the next 12 months?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="planned_layoffs_details"
        detailsPlaceholder="Please provide details about planned layoffs..."
      />
      
      {hasPlannedLayoffs && (
        <div className="pl-5 border-l-2 border-primary/20 space-y-3 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="planned_layoff_count">How many employees do you plan to lay off?</Label>
              <Input
                id="planned_layoff_count"
                type="number"
                {...register('planned_layoff_count')}
                placeholder="Anticipated number of employees"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="planned_layoff_date">When are the layoffs planned?</Label>
              <Input
                id="planned_layoff_date"
                type="date"
                {...register('planned_layoff_date')}
              />
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="voluntary_terminations">Voluntary terminations in the past 12 months</Label>
          <Input
            id="voluntary_terminations"
            type="number"
            {...register('voluntary_terminations')}
            placeholder="Number of voluntary terminations"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="involuntary_terminations">Involuntary terminations in the past 12 months</Label>
          <Input
            id="involuntary_terminations"
            type="number"
            {...register('involuntary_terminations')}
            placeholder="Number of involuntary terminations"
          />
        </div>
      </div>
      
      <RiskQuestion
        id="has_foreign_travel"
        label="Do any employees travel to foreign countries for business purposes?"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      
      {hasForeignTravel && (
        <div className="pl-5 border-l-2 border-primary/20 space-y-3 mt-2">
          <div className="space-y-2">
            <Label htmlFor="foreign_travel_countries">Which countries do employees travel to?</Label>
            <Textarea
              id="foreign_travel_countries"
              {...register('foreign_travel_countries')}
              placeholder="List the countries employees travel to for business..."
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="foreign_travel_frequency">How frequently do employees travel internationally?</Label>
            <Select 
              onValueChange={(value) => setValue('foreign_travel_frequency', value)}
              defaultValue={watch('foreign_travel_frequency') || undefined}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rarely">Rarely (1-2 times per year)</SelectItem>
                <SelectItem value="occasionally">Occasionally (Quarterly)</SelectItem>
                <SelectItem value="frequently">Frequently (Monthly)</SelectItem>
                <SelectItem value="very_frequently">Very Frequently (Weekly)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      
      <RiskQuestion
        id="has_epli_insurance"
        label="Does your company currently have Employment Practices Liability Insurance (EPLI)?"
        register={register}
        setValue={setValue}
        watch={watch}
        detailsField="epli_insurance_details"
        detailsPlaceholder="Please provide details about your EPLI coverage..."
      />
    </div>
  );
};
